import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Lock, CheckCircle2, Play, Award, Rocket, TrendingUp, Crown, ArrowLeft,
  Sparkles, ExternalLink, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ICONS = { rocket: Rocket, 'trending-up': TrendingUp, crown: Crown };

const colorMap = {
  amber: { ring: 'ring-amber-400/40', border: 'border-amber-500/40', bg: 'bg-amber-500/10', text: 'text-amber-300', from: 'from-amber-500/30', glow: 'shadow-[0_0_30px_rgba(251,191,36,0.18)]' },
  emerald: { ring: 'ring-emerald-400/40', border: 'border-emerald-500/40', bg: 'bg-emerald-500/10', text: 'text-emerald-300', from: 'from-emerald-500/30', glow: 'shadow-[0_0_30px_rgba(16,185,129,0.18)]' },
  purple: { ring: 'ring-purple-400/40', border: 'border-purple-500/40', bg: 'bg-purple-500/10', text: 'text-purple-300', from: 'from-purple-500/30', glow: 'shadow-[0_0_30px_rgba(168,85,247,0.18)]' },
};

export default function Learn() {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('guestToken') || '');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const load = async () => {
    setLoading(true);
    try {
      const url = token ? `${API}/elearning/program?token=${token}` : `${API}/elearning/program`;
      const res = await fetch(url);
      const json = await res.json();
      if (json.success) setData(json);
    } catch (e) { toast.error('Could not load program'); }
    finally { setLoading(false); }
  };

  const handleBuy = (item, isBundle = false) => {
    if (!item.has_ready_videos) {
      toast.info("📚 Course content is being prepared — purchase will open as soon as the first lessons are uploaded. Stay tuned!", { duration: 5000 });
      return;
    }
    if (item.payment_link) {
      window.open(item.payment_link, '_blank');
      toast.info('Complete your purchase. Refresh this page when done.');
    } else {
      toast.error('Payment link not configured yet. Please contact support.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-amber-400/30">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-black/95 backdrop-blur-md border-b border-amber-500/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-amber-300 hover:text-amber-200" data-testid="learn-back-home">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Home</span>
          </button>
          <div className="text-center">
            <h1 className="text-sm sm:text-base font-bold bg-gradient-to-r from-amber-300 to-yellow-300 bg-clip-text text-transparent tracking-wider">
              TRADING MASTERY PROGRAM
            </h1>
            <p className="text-[10px] text-amber-200/60 uppercase tracking-widest">FORTHEWISEPRO</p>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={() => navigate('/scorecard')}
            className="border-amber-400/40 text-amber-300 hover:bg-amber-500/10 text-xs"
            data-testid="learn-ai-coach-btn"
          >
            AI Coach →
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-8 text-center">
        <Badge className="bg-amber-500/10 border border-amber-400/30 text-amber-300 mb-5 uppercase tracking-[2px] text-[10px]" data-testid="learn-hero-badge">
          <Sparkles className="w-3 h-3 mr-1.5" /> 3-Part Journey · Recorded Lessons · Certificates
        </Badge>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4">
          From <span className="text-amber-400">zero</span> to <span className="text-emerald-400">funded</span><br />
          on your own terms.
        </h2>
        <p className="text-base text-gray-400 max-w-2xl mx-auto">
          Three progressive parts. Each unlocks the next. Earn a certificate after each level.
          Your AI Coach references every lesson you've completed during live sessions.
        </p>
      </section>

      {/* Coming-soon banner when no part has ready videos yet */}
      {data?.parts?.length > 0 && data.parts.every(p => !p.has_ready_videos) && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 -mb-6">
          <div className="rounded-xl p-4 bg-amber-500/10 border border-amber-400/30 text-center" data-testid="learn-coming-soon-banner">
            <p className="text-sm text-amber-200">
              <span className="font-bold">📚 Course content is on its way.</span> The instructor is recording lessons right now.
              Purchases will open as soon as the first lessons go live. Want to be notified? Use the chat in the corner.
            </p>
          </div>
        </section>
      )}

      {/* Bundle banner */}
      {data?.bundle?.is_active && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 mb-10">
          <div className="rounded-2xl p-5 sm:p-6 bg-gradient-to-r from-amber-500/15 via-emerald-500/10 to-purple-500/15 border-2 border-amber-400/40 shadow-[0_0_45px_rgba(251,191,36,0.12)] flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="learn-bundle-banner">
            <div className="flex-1">
              <p className="text-[10px] text-amber-300 uppercase tracking-[3px] font-bold mb-1">Best value · Save ~17%</p>
              <h3 className="text-2xl sm:text-3xl font-bold">All 3 Parts — Full Program</h3>
              <p className="text-sm text-gray-300 mt-1">
                Unlock everything at once. Skip the wait. Lifetime access to all certificates.
              </p>
            </div>
            <div className="text-center sm:text-right">
              <div className="text-3xl sm:text-4xl font-bold text-amber-300">${data.bundle.price?.toFixed(0)}</div>
              <div className="text-xs text-gray-500 line-through">
                ${data.parts.reduce((s, p) => s + (p.price || 0), 0).toFixed(0)} separately
              </div>
              <Button
                onClick={() => handleBuy(data.bundle, true)}
                disabled={!data.bundle.has_ready_videos}
                className={`mt-3 ${data.bundle.has_ready_videos ? 'bg-amber-500 hover:bg-amber-400 text-black' : 'bg-zinc-800 text-amber-300 border border-amber-500/30 cursor-not-allowed'} font-bold w-full sm:w-auto`}
                data-testid="learn-bundle-buy"
              >
                {data.bundle.has_ready_videos ? <>Get Bundle <ExternalLink className="w-3 h-3 ml-1.5" /></> : 'Available soon'}
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Program parts */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-20 space-y-5">
        {loading && <div className="text-center py-12 text-gray-500">Loading…</div>}
        {!loading && data?.parts?.map((part) => {
          const Icon = ICONS[part.icon] || Rocket;
          const c = colorMap[part.color] || colorMap.amber;
          return (
            <Card key={part.id} className={`bg-zinc-950/80 border-2 ${c.border} ${part.unlocked ? c.glow : ''} transition-all`} data-testid={`learn-part-${part.slug}`}>
              <CardContent className="p-5 sm:p-7">
                <div className="flex items-start gap-4 sm:gap-6">
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl ${c.bg} ${c.border} border flex items-center justify-center shrink-0`}>
                    <Icon className={`w-7 h-7 sm:w-8 sm:h-8 ${c.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className={`text-[10px] uppercase tracking-[3px] font-bold ${c.text} mb-1`}>
                          Part {part.order}
                        </p>
                        <h3 className="text-xl sm:text-2xl font-bold leading-tight text-white" data-testid={`learn-part-${part.slug}-title`}>{part.title}</h3>
                        <p className="text-sm text-gray-400 mt-1">{part.tagline}</p>
                      </div>
                      <div className="text-right">
                        {part.owned ? (
                          <Badge className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px]">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Owned
                          </Badge>
                        ) : (
                          <div className="text-2xl font-bold text-white">${part.price?.toFixed(0)}</div>
                        )}
                      </div>
                    </div>

                    {/* Status row */}
                    <div className="mt-4 flex items-center gap-3 flex-wrap text-xs">
                      <div className="text-gray-400">
                        {part.lessons_count || 0} {part.lessons_count === 1 ? 'lesson' : 'lessons'}
                      </div>
                      {part.unlocked && (
                        <>
                          <div className={`flex items-center gap-1.5 ${c.text}`}>
                            <div className="w-32 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                              <div className={`h-full bg-gradient-to-r ${c.from} to-emerald-400`} style={{ width: `${part.completion_pct}%` }} />
                            </div>
                            <span className="font-mono">{part.completion_pct.toFixed(0)}%</span>
                          </div>
                          {part.certificate && (
                            <Badge className="bg-amber-500/15 border border-amber-400/40 text-amber-300 text-[10px]">
                              <Award className="w-3 h-3 mr-1" /> Certified
                            </Badge>
                          )}
                        </>
                      )}
                      {!part.unlocked && part.lock_reason === 'complete_previous' && (
                        <Badge variant="outline" className="border-gray-700 text-gray-400 text-[10px]">
                          <Lock className="w-3 h-3 mr-1" /> Finish Part {part.order - 1} first
                          {part.prev_pct != null && ` (${part.prev_pct.toFixed(0)}%)`}
                        </Badge>
                      )}
                      {!part.unlocked && part.lock_reason === 'buy_to_unlock' && (
                        <Badge variant="outline" className="border-amber-500/30 text-amber-300 text-[10px]">
                          <Lock className="w-3 h-3 mr-1" /> Locked — Purchase to unlock
                        </Badge>
                      )}
                      {!part.unlocked && part.lock_reason === 'sign_in_required' && (
                        <Badge variant="outline" className="border-amber-500/30 text-amber-300 text-[10px]">
                          Sign in to access
                        </Badge>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="mt-5 flex flex-wrap gap-2">
                      {part.unlocked ? (
                        <Button
                          onClick={() => navigate(`/learn/${part.slug}`)}
                          className={`bg-gradient-to-r ${c.from} to-zinc-800 hover:opacity-90 ${c.text} border ${c.border}`}
                          data-testid={`learn-part-${part.slug}-open`}
                        >
                          <Play className="w-4 h-4 mr-2" />
                          {part.completion_pct > 0 ? 'Continue learning' : 'Start now'}
                        </Button>
                      ) : part.free_preview_count > 0 ? (
                        <Button
                          onClick={() => navigate(`/learn/${part.slug}`)}
                          variant="outline"
                          className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 font-bold"
                          data-testid={`learn-part-${part.slug}-preview`}
                        >
                          <Play className="w-4 h-4 mr-2" /> Watch free preview ({part.free_preview_count})
                        </Button>
                      ) : null}
                      {!part.unlocked && part.can_buy && (
                        part.has_ready_videos ? (
                          <Button
                            onClick={() => handleBuy(part)}
                            className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
                            data-testid={`learn-part-${part.slug}-buy`}
                          >
                            Unlock for ${part.price?.toFixed(0)} <ExternalLink className="w-3 h-3 ml-1.5" />
                          </Button>
                        ) : (
                          <Button
                            disabled
                            className="bg-zinc-800 text-amber-300 border border-amber-500/30 cursor-not-allowed"
                            data-testid={`learn-part-${part.slug}-soon`}
                          >
                            <Clock className="w-4 h-4 mr-2" /> Available soon
                          </Button>
                        )
                      )}
                      {!part.unlocked && !part.can_buy && part.free_preview_count === 0 && (
                        <Button disabled variant="outline" className="border-gray-700 text-gray-500 cursor-not-allowed" data-testid={`learn-part-${part.slug}-locked`}>
                          <Lock className="w-4 h-4 mr-2" /> Locked
                        </Button>
                      )}
                      {part.certificate && (
                        <Button
                          onClick={() => window.open(`${API}/elearning/certificate/${part.certificate.id}.pdf`, '_blank')}
                          variant="outline"
                          className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                          data-testid={`learn-part-${part.slug}-cert`}
                        >
                          <Award className="w-4 h-4 mr-2" /> View Certificate
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {!loading && !token && (
          <div className="rounded-xl p-5 bg-zinc-950 border border-amber-500/20 text-center">
            <p className="text-sm text-gray-400 mb-3">
              Sign in with your FORTHEWISEPRO account to track your progress and unlock purchased lessons.
            </p>
            <Button
              onClick={() => navigate('/portal')}
              className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
              data-testid="learn-signin-cta"
            >
              Sign in
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
